import { alpha, type Theme } from "@mui/material";
import type { TipoDia } from "@/api/escala.service";
import { mix } from "@/theme";

export const ROTULO_TIPO: Record<Exclude<TipoDia, "">, string> = {
  T: "Trabalha",
  F: "Folga",
  FA: "Folga feriado",
  C: "Compensação",
  A: "Afastado",
  D: "Desligado",
};

export const TIPOS_LEGENDA = Object.keys(ROTULO_TIPO) as Exclude<TipoDia, "">[];

// Feriado tem cor própria pra não se confundir com o domingo (faixa neutra).
export const COR_FERIADO = "#0d9488";

// Cores fixas da impressão (papel branco, independente do modo claro/escuro do app), as mesmas
// do modo claro da grade de tela.
export const CORES_IMPRESSAO: Record<string, { bg: string; fg: string }> = {
  T: { bg: "#86efac", fg: "#14532d" },
  F: { bg: "#ef4444", fg: "#ffffff" },
  FA: { bg: "#a855f7", fg: "#ffffff" },
  C: { bg: "#3b82f6", fg: "#ffffff" },
  A: { bg: "#fbbf24", fg: "#451a03" },
  D: { bg: "#475569", fg: "#ffffff" },
};

// Fundos da escalinha em preto e branco: o do cabeçalho da tabela e o do domingo e do feriado.
export const FUNDO_CABECALHO_IMPRESSAO = "#f0f0f0";
export const FUNDO_DOMINGO_IMPRESSAO = "#e0e0e0";

// Faixas de domingo e de feriado da impressão colorida, a da escala e a da escalinha.
export const FAIXA_DOMINGO_IMPRESSAO = "#f0f0f0";
export const FAIXA_FERIADO_IMPRESSAO = alpha(COR_FERIADO, 0.2);

export interface EstiloTipo {
  bg: string;
  fg: string;
  peso: number;
}

// Rótulo de um tipo vindo do servidor; um tipo novo cai no próprio código.
export const rotuloDoTipo = (tipo: string) => (ROTULO_TIPO as Record<string, string>)[tipo] ?? tipo;

// O T do tema escuro é esse verde em transparência sobre o papel.
const VERDE_T_ESCURO = "#22c55e";
const OPACIDADE_T_ESCURO = 0.4;

// Fundo contra o qual a hora do T é ajustada (corDaJornada): só no tema escuro, onde a cor
// escolhida para a tela clara não se leria. Na tela clara, a cor sai como foi escolhida.
export const fundoDaHora = (theme: Theme) =>
  theme.palette.mode === "dark" ? mix(theme.palette.background.paper, VERDE_T_ESCURO, OPACIDADE_T_ESCURO) : undefined;

// Paleta própria (não a do tema): a marca do client pode ser verde ou vermelha
// e a grade precisa de matizes que nunca se confundam. Só o T é claro, por ser
// a maioria da grade; o resto vem em cor cheia.
export function estiloTipo(theme: Theme, tipo: TipoDia): EstiloTipo {
  const escuro = theme.palette.mode === "dark";
  switch (tipo) {
    case "T":
      return escuro
        ? { bg: alpha(VERDE_T_ESCURO, OPACIDADE_T_ESCURO), fg: "#dcfce7", peso: 600 }
        : { bg: "#86efac", fg: "#14532d", peso: 600 };
    case "F":
      return { bg: escuro ? "#dc2626" : "#ef4444", fg: "#ffffff", peso: 800 };
    case "FA":
      return { bg: escuro ? "#9333ea" : "#a855f7", fg: "#ffffff", peso: 800 };
    case "C":
      return { bg: escuro ? "#2563eb" : "#3b82f6", fg: "#ffffff", peso: 800 };
    case "A":
      return { bg: escuro ? "#d97706" : "#fbbf24", fg: escuro ? "#ffffff" : "#451a03", peso: 800 };
    case "D":
      return { bg: escuro ? "#64748b" : "#475569", fg: "#ffffff", peso: 800 };
    default:
      return { bg: "transparent", fg: theme.palette.text.disabled, peso: 400 };
  }
}

// Calculado uma vez por tema e repassado às linhas, em vez de uma função de
// estilo por célula.
export function estilosDoTema(theme: Theme): Record<string, EstiloTipo> {
  const estilos: Record<string, EstiloTipo> = { "": estiloTipo(theme, "") };
  for (const tipo of TIPOS_LEGENDA) estilos[tipo] = estiloTipo(theme, tipo);
  return estilos;
}

const ROTULO_REGRA: Record<string, string> = {
  MAIS_DE_7_DIAS_CONSECUTIVOS: "7 dias seguidos trabalhados",
  MAIS_DE_2_DOMINGOS_CONSECUTIVOS: "mais de 2 domingos seguidos",
  MULHER_DOMINGOS_SEGUIDOS: "mulher com dois domingos seguidos trabalhados (art. 386)",
  COMPENSACAO_EM_FERIADO: "compensação em feriado",
  GRUPO_ESTRATEGICO_AUSENTE_DUPLO: "dois do grupo estratégico fora no mesmo dia",
  DIA_SEM_HORARIO: "dia trabalhado sem horário definido",
  INTERJORNADA_MENOR_11H: "menos de 11h de descanso entre jornadas",
  DESCANSO_SEMANAL_MENOR_35H: "menos de 35h de descanso semanal",
  FOLGAS_NA_SEMANA_DIFERENTE_DE_2: "semana sem 2 folgas",
};

// Rótulo de uma regra do validador; uma regra nova cai no próprio código.
export const rotuloDaRegra = (regra: string) => ROTULO_REGRA[regra] ?? regra;
